import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { getTheme, themed, Flow, Div, Box,Button, Loader, Text as Tx, } from "@bracket/ui";
import { useState, useMemo, useEffect, } from "react";
import styled from "styled-components";
import { Notification } from "./Notification";
import { NotificationTabs } from "./NotificationTabs";

import { getAccount } from "wagmi/actions";
import { wagmiConfig } from "@wagmiconfig";

import { UPDATE_NOTIFICATIONS_STATUS } from "@gql/mutations";
import { createNotificationTabsConfig } from "./utils";
import { useMessaging } from "@bracket/platform/store";
import type { NotificationTabsIds } from "./types";
import type { CommonNotification, Notifications_StatusMutation } from "@gql/types/graphql";

export const Notifications: React.FC = () => {
	themed();

	const [activeTab, setActiveTab] = useState<NotificationTabsIds>("unread");
	const { address } = getAccount(wagmiConfig);
	const { fetching, messages, refetch, ready, fetch:fetchMessages, } = useMessaging();
	const data = { notifications: messages };

	const filteredNotifications = useMemo(() => {
		return (data?.notifications ?? []).reduce<{
			all: CommonNotification[];
			unread: CommonNotification[];
		}>(
			(acc, notification) => {
				if (notification.status !== "INACTIVE" && notification.status !== "READ") {
					acc.unread.push(notification);
				}

				acc.all.push(notification);

				return acc;
			},
			{ all: [], unread: [] },
		);
	}, [data?.notifications]);

	const [updateNotificationStatus, { loading: updateNotificationStatusLoading }] = useMutation<Notifications_StatusMutation>(UPDATE_NOTIFICATIONS_STATUS, {
		variables: {
			status: JSON.stringify(
				filteredNotifications.unread.reduce<Record<string, "READ">>((acc, notification) => {
					acc[notification.id] = "READ";
					return acc;
				}, {}),
			),
		},
		context: {
			headers: {
				wallet: address,
			},
		},
	});

	const notificationTabsConfig = createNotificationTabsConfig(data?.notifications?.length ?? 0, filteredNotifications.unread.length);

	const notifications = activeTab === "unread" ? filteredNotifications.unread : filteredNotifications.all;

	useEffect(() => {
		if(!ready && !fetching) {
			fetchMessages();
		}
	}, [ready, fetching,])

	if(fetching || !ready) {
		return <Loader center />;
	}

	return (
		<NotificationContainer>
			<NotificationHeaderContainer>
				<Tx h5 color="heading">Notifications</Tx>
				<NotificationTabs activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab)} items={notificationTabsConfig} />
			</NotificationHeaderContainer>
			<Divider />
			<NotificationItemsContainer>
				{notifications
					.sort((a, b) => (a.created > b.created ? -1 : 1))
					.map((notification) => (
						<Notification key={notification.id} id={notification.id} title={notification.title} iconName={"page-bars"} created={notification.created} body={notification.body} actionLabel={notification.action_label} actionUrl={notification.action_url} onRead={refetch} displayRead={notification.status !== "READ"} />
					))}
			</NotificationItemsContainer>
			<Divider />
			{filteredNotifications.unread.length > 0 && (
				<BtnContainer>
					<Button label="Mark all as read" variant="primary-outline" size="md"
						onClick={async () => {
							await updateNotificationStatus();
							await refetch();
						}}
						busy={updateNotificationStatusLoading}
					/>
				</BtnContainer>
			)}
		</NotificationContainer>
	);
};

const NotificationContainer = styled(Flow)`
	width: 100%;
  background-color: ${() => getTheme().surface_container};
  color: ${() => getTheme().surface_on};
  border-radius: ${() => getTheme().radials.sm};
	padding: 1rem;
`;

const NotificationHeaderContainer = styled(Box)`
  align-items: center;
	gap: 1rem;
`;

const NotificationItemsContainer = styled(Flow)`
	padding: 0 32px;
`;

const Divider = styled(Div)`
  border-bottom: 1px solid ${() => getTheme().outline_variant};
`;

const BtnContainer = styled(Div)`
  padding: 20px 12px;
`;


